import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChartContainer, ChartLegend, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { supabase } from "@/integrations/supabase/client";
import { normalizeCouponCode, resolvePlanId, calculateDiscountedAmount } from "@/services/couponService";
import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

type RaioXModalProps = { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  coupon: any | null;
  children?: React.ReactNode;
};

export function RaioXModal({ open, onOpenChange, coupon }: RaioXModalProps) {
  const [loading, setLoading] = useState(false);
  const [usages, setUsages] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [clientsPage, setClientsPage] = useState<number>(1);
  const [clientsPageSize, setClientsPageSize] = useState<number>(10);
  const [txSearch, setTxSearch] = useState("");
  const [txMethodFilter, setTxMethodFilter] = useState<string>("all");
  const [txStatusFilter, setTxStatusFilter] = useState<string>("all");
  const [txPage, setTxPage] = useState<number>(1);
  const [txPageSize, setTxPageSize] = useState<number>(10);
  const [couponCode, setCouponCode] = useState<string>("");
  const [planSlug, setPlanSlug] = useState<string>("plus");
  const [validation, setValidation] = useState<{ is_valid: boolean; discounted_amount?: number; must_pay?: boolean; description?: string; image_url?: string; error?: string } | null>(null);
  const { tenantId } = useTenant();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{ type: 'single' | 'bulk'; usageIds: string[]; title: string } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !coupon?.code) return;
      setLoading(true);
      try {
        const { data: foundCoupon } = await supabase.from("subscription_coupons").select("id, code, description, activation_type, discount_type, discount_value, duration_unit, duration_amount, start_at, end_at, status").eq("code", coupon.code).maybeSingle();
        const couponId = foundCoupon?.id ?? coupon.id;
        const { data: u } = await supabase
          .from("subscription_coupon_usages")
          .select("id, coupon_id, tenant_id, user_id, subscription_transaction_id, times_used, used_at")
          .eq("coupon_id", couponId)
          .order("used_at", { ascending: false });
        setUsages(u || []);

        const txnIds = (u || []).map((x: any) => x.subscription_transaction_id).filter(Boolean);
        let txns: any[] = [];
        if (txnIds.length > 0) {
          const { data: t } = await supabase
            .from("subscription_transactions")
            .select("id, tenant_id, payment_method, amount, status, payment_date, created_at, metadata, subscription_plan_id")
            .in("id", txnIds);
          txns = t || [];
        }
        setTransactions(txns);

        const tenantIds = Array.from(new Set((u || []).map((x: any) => x.tenant_id).filter(Boolean)));
        let ts: any[] = [];
        if (tenantIds.length > 0) {
          const { data: tnts } = await supabase.from("tenants").select("id, name, email, subscription_status, subscription_plan_id").in("id", tenantIds);
          ts = tnts || [];
        }
        setTenants(ts);
        setCouponCode(coupon.code);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, coupon?.code, coupon?.id]);

  // Usar funções centralizadas do service
  
  /**
   * Resolve plano por slug e retorna dados completos incluindo preço
   * Usa serviço centralizado para resolver ID e busca dados do plano
   */
  const resolvePlanBySlug = async (slug: string) => {
    const planId = await resolvePlanId(slug);
    const { data } = await supabase
      .from('subscription_plans')
      .select('id, price')
      .eq('id', planId)
      .maybeSingle();
    return data || null;
  };

  const handleValidate = async () => {
    setValidation(null);
    const code = normalizeCouponCode(couponCode);
    const { data: c } = await supabase.from('subscription_coupons').select('*').eq('code', code).maybeSingle();
    if (!c) { setValidation({ is_valid: false, error: 'Cupom não encontrado' }); return; }
    const now = new Date();
    const startOk = !c.start_at || new Date(c.start_at) <= now;
    const endOk = !c.end_at || new Date(c.end_at) >= now;
    if (!(c.status === 'active' && startOk && endOk)) { setValidation({ is_valid: false, error: 'Fora de validade ou inativo' }); return; }
    const plan = await resolvePlanBySlug(planSlug);
    if (!plan?.id) { setValidation({ is_valid: false, error: 'Plano inválido' }); return; }
    let plansOk = true;
    if (Array.isArray(c.allowed_plan_ids) && c.allowed_plan_ids.length > 0) {
      plansOk = c.allowed_plan_ids.includes(plan.id) || c.allowed_plan_ids.includes('any') || c.allowed_plan_ids.includes('all');
    }
    if (!plansOk) { setValidation({ is_valid: false, error: 'Não permitido para este plano' }); return; }
    const price = Number(plan.price || 0);
    // Usar função centralizada para calcular desconto
    const discounted_amount = calculateDiscountedAmount(price, c.discount_type, c.discount_value);
    const must_pay = c.activation_type === 'regular' && c.discount_type !== 'full';
    setValidation({ is_valid: true, discounted_amount, must_pay, description: c.description, image_url: c.image_url });
  };

  const usageByDay = useMemo(() => {
    const map: Record<string, number> = {};
    (usages || []).forEach((u) => {
      const d = new Date(u.used_at).toISOString().slice(0, 10);
      map[d] = (map[d] || 0) + (u.times_used || 1);
    });
    return Object.entries(map).sort(([a], [b]) => (a > b ? 1 : -1)).map(([date, count]) => ({ date, count }));
  }, [usages]);

  const usageByTenant = useMemo(() => {
    const map: Record<string, number> = {};
    (usages || []).forEach((u) => {
      const id = u.tenant_id || "sem-tenant";
      map[id] = (map[id] || 0) + (u.times_used || 1);
    });
    return Object.entries(map).map(([tenant_id, count]) => ({ tenant_id, count }));
  }, [usages]);

  const paymentSplit = useMemo(() => {
    const map: Record<string, number> = { pix: 0, credit_card: 0, coupon: 0 };
    (transactions || []).forEach((t) => { map[t.payment_method || 'unknown'] = (map[t.payment_method || 'unknown'] || 0) + 1; });
    return Object.entries(map).map(([method, value]) => ({ method, value }));
  }, [transactions]);

  const totalApproved = useMemo(() => (transactions || []).filter((t) => t.status === 'approved').length, [transactions]);
  const totalPending = useMemo(() => (transactions || []).filter((t) => t.status === 'pending').length, [transactions]);
  const totalRejected = useMemo(() => (transactions || []).filter((t) => t.status === 'rejected').length, [transactions]);

  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);
  const tenantsDisplay = useMemo(() => {
    const byId: Record<string, any> = {};
    tenants.forEach((t) => { byId[t.id] = t; });
    const txByTenant: Record<string, any[]> = {};
    (transactions || []).forEach((tx) => {
      txByTenant[tx.tenant_id] = txByTenant[tx.tenant_id] || [];
      txByTenant[tx.tenant_id].push(tx);
    });
    const usageGrouped: Record<string, any[]> = {};
    (usages || []).forEach((u) => {
      usageGrouped[u.tenant_id] = usageGrouped[u.tenant_id] || [];
      usageGrouped[u.tenant_id].push(u);
    });
    let rows = Object.entries(usageGrouped).map(([tenant_id, list]) => {
      const tenant = byId[tenant_id];
      const txs = txByTenant[tenant_id] || [];
      const anyMethod = txs[0]?.payment_method || 'coupon';
      return { tenant, tenant_id, count: list.length, usages: list, methods: txs.map((t)=>t.payment_method) };
    });
    const term = search.trim().toLowerCase();
    rows = rows.filter((r) => {
      const matchesSearch = !term || (r.tenant?.name || '').toLowerCase().includes(term) || String(r.tenant_id).toLowerCase().includes(term);
      const matchesMethod = methodFilter === 'all' || r.methods.includes(methodFilter);
      return matchesSearch && matchesMethod;
    });
    const total = rows.length;
    const start = (clientsPage - 1) * clientsPageSize;
    const pageRows = rows.slice(start, start + clientsPageSize);
    return { rows: pageRows, total };
  }, [tenants, transactions, usages, search, methodFilter, clientsPage, clientsPageSize]);

  const tenantsAgg = useMemo(() => {
    const byId: Record<string, any> = {};
    tenants.forEach((t) => { byId[t.id] = t; });
    const usageGrouped: Record<string, number> = {};
    (usages || []).forEach((u) => { usageGrouped[u.tenant_id] = (usageGrouped[u.tenant_id] || 0) + 1; });
    return Object.entries(usageGrouped).map(([tenant_id, count]) => ({ tenant: byId[tenant_id], tenant_id, count }));
  }, [tenants, usages]);

  const handleRevertUsage = async (usageId: string) => {
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || (window as any).SUPABASE_URL || "";
    const anonKey = (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY || (window as any).SUPABASE_ANON_KEY || "";
    const { data: sessionRes } = await supabase.auth.getSession();
    const userJwt = sessionRes?.session?.access_token || "";
    let ok = false;
    if (supabaseUrl) {
      const resp = await fetch(`${String(supabaseUrl).replace(/\/+$/, '')}/functions/v1/subscription_coupons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userJwt ? { Authorization: `Bearer ${userJwt}` } : { Authorization: `Bearer ${anonKey}` }),
        },
        body: JSON.stringify({ action: 'revert_usage', usage_id: usageId }),
      }).catch(() => null as any);
      if (resp && resp.ok) {
        const j = await resp.json().catch(() => ({}));
        ok = Boolean(j?.success);
      }
    }

    if (!ok) {
      let apiKeyFull: string | null = null;
      if (tenantId) {
        const { data: key } = await supabase
          .from('api_keys' as any)
          .select('id, key_full, key_prefix')
          .eq('tenant_id', tenantId)
          .eq('type', 'tenant')
          .eq('enabled', true)
          .order('last_used_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        apiKeyFull = (key?.key_full as string) || null;
        if (!apiKeyFull && key?.id) {
          const res = await supabase.functions.invoke('api-keys-get-secret', { body: { id: key.id } });
          const secret = (res.data?.secret as string) || null;
          if (secret && key?.key_prefix) apiKeyFull = `${key.key_prefix}.${secret}`;
        }
      }
      if (supabaseUrl && apiKeyFull) {
        const resp2 = await fetch(`${String(supabaseUrl).replace(/\/+$/, '')}/functions/v1/subscription_coupons`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKeyFull}`,
          },
          body: JSON.stringify({ action: 'revert_usage', usage_id: usageId }),
        }).catch(() => null as any);
        if (!resp2 || !resp2.ok) return;
        const j2 = await resp2.json().catch(() => ({}));
        ok = Boolean(j2?.success);
        if (!ok) return;
      } else {
        const { data, error } = await supabase.functions.invoke('subscription_coupons', {
          headers: apiKeyFull ? { Authorization: `Bearer ${apiKeyFull}` } : (userJwt ? { Authorization: `Bearer ${userJwt}` } : { Authorization: `Bearer ${anonKey}` }),
          body: { action: 'revert_usage', usage_id: usageId },
        });
        if (error || !data?.success) return;
      }
    }
    // Refresh modal data
    const fetchAgain = async () => {
      if (!coupon?.code) return;
      const { data: foundCoupon } = await supabase.from("subscription_coupons").select("id").eq("code", coupon.code).maybeSingle();
      const couponId = foundCoupon?.id ?? coupon.id;
      const { data: u } = await supabase
        .from("subscription_coupon_usages")
        .select("id, coupon_id, tenant_id, user_id, subscription_transaction_id, times_used, used_at")
        .eq("coupon_id", couponId)
        .order("used_at", { ascending: false });
      setUsages(u || []);
      const txnIds = (u || []).map((x: any) => x.subscription_transaction_id).filter(Boolean);
      if (txnIds.length > 0) {
        const { data: t } = await supabase
          .from("subscription_transactions")
          .select("id, tenant_id, payment_method, amount, status, payment_date, created_at, metadata, subscription_plan_id")
          .in("id", txnIds);
        setTransactions(t || []);
      } else {
        setTransactions([]);
      }
    };
    await fetchAgain();
  };

  const askRemoveUsage = (usageId: string) => {
    setConfirmPayload({ type: 'single', usageIds: [usageId], title: 'Remover uso do cupom' });
    setConfirmOpen(true);
  };

  const askClearAll = (row: any) => {
    const ids = (row.usages || []).map((u: any) => u.id);
    setConfirmPayload({ type: 'bulk', usageIds: ids, title: `Limpar todos os usos` });
    setConfirmOpen(true);
  };

  const confirmExecute = async () => {
    if (!confirmPayload) return;
    setConfirmLoading(true);
    try {
      await Promise.all(confirmPayload.usageIds.map((id) => handleRevertUsage(id)));
      setConfirmOpen(false);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden">
        <div className="flex flex-col max-h-[90vh]">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b">
            <DialogTitle>Raio-X do Cupom {coupon?.code}</DialogTitle>
            <DialogDescription>Visualize e gerencie usos do cupom, clientes e transações.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="md:col-span-1">
                <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Código do cupom" />
              </div>
              <div className="md:col-span-1">
                <select className="border rounded px-2 py-2 w-full" value={planSlug} onChange={(e) => setPlanSlug(e.target.value)}>
                  <option value="plus">Plano Plus</option>
                  <option value="plus_whats">Plano Plus Whats</option>
                  <option value="plus_whats_ia">Plano Plus Whats IA</option>
                </select>
              </div>
              <div className="md:col-span-1 flex items-center justify-end">
                <Button onClick={handleValidate}>Validar Cupom</Button>
              </div>
            </div>

            {validation && (
              <div className="mb-6">
                {validation.is_valid ? (
                  <div className="flex items-center justify-between p-4 border rounded bg-muted">
                    <div>
                      <div className="text-sm text-muted-foreground">Total com desconto (primeiro ciclo)</div>
                      <div className="text-3xl font-bold">{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(validation.discounted_amount || 0)}</div>
                    </div>
                    <div className="text-sm">
                      {validation.must_pay ? <Badge>Pagamento necessário</Badge> : <Badge variant="secondary">Isento</Badge>}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border rounded bg-destructive/10 text-destructive text-sm">{validation.error || 'Cupom inválido'}</div>
                )}
              </div>
            )}

            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                <TabsTrigger value="usage">Uso</TabsTrigger>
                <TabsTrigger value="tenants">Clientes</TabsTrigger>
                <TabsTrigger value="transactions">Transações</TabsTrigger>
                <TabsTrigger value="plans">Planos</TabsTrigger>
                <TabsTrigger value="timeline">Linha do Tempo</TabsTrigger>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Resumo</CardTitle>
                      <CardDescription>Dados principais do cupom</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>Código</div><div className="font-semibold">{coupon?.code}</div>
                        <div>Ativação</div><div>{coupon?.activation_type}</div>
                        <div>Tipo de Desconto</div><div>{coupon?.discount_type}</div>
                        <div>Valor</div><div>{coupon?.discount_type === 'fixed' ? new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(coupon?.discount_value||0) : coupon?.discount_type === 'percentage' ? `${coupon?.discount_value || 0}%` : '-'}</div>
                        <div>Duração</div><div>{coupon?.duration_amount} {coupon?.duration_unit === 'months' ? 'meses' : 'dias'}</div>
                        <div>Janela</div><div>{new Date(coupon?.start_at).toLocaleString('pt-BR')} — {new Date(coupon?.end_at).toLocaleString('pt-BR')}</div>
                        <div>Status</div><div><Badge variant={coupon?.status==='active'?'default':'secondary'}>{coupon?.status}</Badge></div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>KPIs</CardTitle>
                      <CardDescription>Indicadores de uso e conversão</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Usos</div><div className="text-xl font-bold">{usages.length}</div></div>
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Aprovadas</div><div className="text-xl font-bold">{totalApproved}</div></div>
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Pendentes</div><div className="text-xl font-bold">{totalPending}</div></div>
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Rejeitadas</div><div className="text-xl font-bold">{totalRejected}</div></div>
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Tenants Impactados</div><div className="text-xl font-bold">{new Set(usages.map((u)=>u.tenant_id).filter(Boolean)).size}</div></div>
                        <div className="bg-muted rounded p-3"><div className="text-xs text-muted-foreground">Ticket Médio 1º ciclo</div><div className="text-xl font-bold">{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format((transactions||[]).reduce((s,t)=>s+(t.amount||0),0) / Math.max(1, transactions.length))}</div></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="usage">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader><CardTitle>Usos por Dia</CardTitle></CardHeader>
                    <CardContent>
                      <ChartContainer config={{ count: { label: 'Usos', color: '#4f46e5' } }}>
                        <LineChart data={usageByDay}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <ChartLegend />
                          <Line type="monotone" dataKey="count" stroke="#4f46e5" />
                        </LineChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle>Método de Pagamento</CardTitle></CardHeader>
                    <CardContent>
                      <ChartContainer config={{ pix: { label: 'PIX', color: '#16a34a' }, credit_card: { label: 'Cartão', color: '#ea580c' }, coupon: { label: 'Isenção', color: '#3b82f6' } }}>
                        <BarChart data={paymentSplit}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="method" />
                          <YAxis />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <ChartLegend />
                          <Bar dataKey="value" fill="#3b82f6" />
                        </BarChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="tenants">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="relative w-full md:w-[280px]">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input value={search} onChange={(e) => { setSearch(e.target.value); setClientsPage(1); }} placeholder="Buscar cliente" className="pl-8" />
                  </div>
                  <select className="border rounded px-2 py-2" value={methodFilter} onChange={(e)=>{ setMethodFilter(e.target.value); setClientsPage(1); }}>
                    <option value="all">Todos métodos</option>
                    <option value="coupon">Isenção</option>
                    <option value="pix">PIX</option>
                    <option value="credit_card">Cartão</option>
                  </select>
                  <select className="border rounded px-2 py-2" value={clientsPageSize} onChange={(e)=>{ setClientsPageSize(Number(e.target.value)); setClientsPage(1); }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Usos</TableHead>
                        <TableHead>Plano Atual</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenantsDisplay.rows.map((row: any) => (
                        <React.Fragment key={row.tenant_id}>
                          <TableRow onClick={()=> setExpandedTenantId(expandedTenantId === row.tenant_id ? null : row.tenant_id)} className="cursor-pointer">
                            <TableCell>{row.tenant?.name || row.tenant_id}</TableCell>
                            <TableCell>{row.count}</TableCell>
                            <TableCell>{row.tenant?.subscription_plan_id || '—'}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={(e)=>{ e.stopPropagation(); askClearAll(row); }}>Limpar todos</Button>
                            </TableCell>
                          </TableRow>
                          {expandedTenantId === row.tenant_id && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <div className="rounded-md border p-2">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Data</TableHead>
                                        <TableHead>Método</TableHead>
                                        <TableHead>Plano</TableHead>
                                        <TableHead className="text-right">Ações</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {row.usages.map((u:any)=>{
                                        const tx = (transactions||[]).find((t:any)=> t.id === u.subscription_transaction_id);
                                        return (
                                          <TableRow key={u.id}>
                                            <TableCell>{new Date(u.used_at).toLocaleString('pt-BR')}</TableCell>
                                            <TableCell>{tx?.payment_method || '—'}</TableCell>
                                            <TableCell>{tx?.subscription_plan_id || '—'}</TableCell>
                                            <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={()=> askRemoveUsage(u.id)}>Remover</Button></TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <div>Exibindo {(tenantsDisplay.rows.length)} de {tenantsDisplay.total}</div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={()=> setClientsPage(Math.max(1, clientsPage - 1))}>Anterior</Button>
                    <span>Página {clientsPage}</span>
                    <Button variant="outline" size="sm" onClick={()=> setClientsPage(clientsPage + 1)} disabled={(clientsPage * clientsPageSize) >= tenantsDisplay.total}>Próxima</Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="transactions">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="relative w-full md:w-[280px]">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input value={txSearch} onChange={(e)=>{ setTxSearch(e.target.value); setTxPage(1); }} placeholder="Buscar por plano ou método" className="pl-8" />
                  </div>
                  <select className="border rounded px-2 py-2" value={txMethodFilter} onChange={(e)=>{ setTxMethodFilter(e.target.value); setTxPage(1); }}>
                    <option value="all">Todos métodos</option>
                    <option value="coupon">Isenção</option>
                    <option value="pix">PIX</option>
                    <option value="credit_card">Cartão</option>
                  </select>
                  <select className="border rounded px-2 py-2" value={txStatusFilter} onChange={(e)=>{ setTxStatusFilter(e.target.value); setTxPage(1); }}>
                    <option value="all">Todos status</option>
                    <option value="approved">Aprovado</option>
                    <option value="pending">Pendente</option>
                    <option value="rejected">Rejeitado</option>
                  </select>
                  <select className="border rounded px-2 py-2" value={txPageSize} onChange={(e)=>{ setTxPageSize(Number(e.target.value)); setTxPage(1); }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Plano</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const term = txSearch.trim().toLowerCase();
                        const filtered = (transactions || []).filter((t) => {
                          const matchesTerm = !term || String(t.subscription_plan_id||'').toLowerCase().includes(term) || String(t.payment_method||'').toLowerCase().includes(term);
                          const matchesMethod = txMethodFilter === 'all' || t.payment_method === txMethodFilter;
                          const matchesStatus = txStatusFilter === 'all' || t.status === txStatusFilter;
                          return matchesTerm && matchesMethod && matchesStatus;
                        });
                        const total = filtered.length;
                        const start = (txPage - 1) * txPageSize;
                        const pageRows = filtered.slice(start, start + txPageSize);
                        return pageRows.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>{(t.payment_date || t.created_at) ? new Date(t.payment_date || t.created_at).toLocaleString('pt-BR') : '—'}</TableCell>
                            <TableCell>{t.payment_method}</TableCell>
                            <TableCell><Badge variant={t.status==='approved'?'default':'secondary'}>{t.status}</Badge></TableCell>
                            <TableCell>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(t.amount||0)}</TableCell>
                            <TableCell>{t.subscription_plan_id || '—'}</TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-end mt-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={()=> setTxPage(Math.max(1, txPage - 1))}>Anterior</Button>
                    <span>Página {txPage}</span>
                    <Button variant="outline" size="sm" onClick={()=> setTxPage(txPage + 1)}>Próxima</Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="plans">
                <Card>
                  <CardHeader>
                    <CardTitle>Planos Alvo</CardTitle>
                    <CardDescription>Planos permitidos e cobertura</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm">{Array.isArray(coupon?.allowed_plan_ids) && coupon?.allowed_plan_ids.length>0 ? coupon.allowed_plan_ids.join(', ') : 'Qualquer plano'}</div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="timeline">
                <div className="space-y-3">
                  <Card>
                    <CardHeader><CardTitle>Linha do Tempo de Usos</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="text-sm space-y-1">
                        {(usages||[]).map((u)=> (
                          <li key={u.id}>{new Date(u.used_at).toLocaleString('pt-BR')} • Tenant {u.tenant_id || '—'} • Tx {u.subscription_transaction_id || '—'}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="analytics">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader><CardTitle>Distribuição de Status</CardTitle></CardHeader>
                    <CardContent>
                      <PieChart width={380} height={220}>
                        <Pie data={[{name:'Aprovado', value: totalApproved},{name:'Pendente', value: totalPending},{name:'Rejeitado', value: totalRejected}]} dataKey="value" nameKey="name" outerRadius={80}>
                          {["#22c55e", "#f59e0b", "#ef4444"].map((c,i)=>(<Cell key={i} fill={c} />))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle>Top Clientes por Uso</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="text-sm space-y-1">
                        {tenantsAgg.sort((a,b)=>b.count-a.count).slice(0,8).map((x,i)=>(<li key={i}>{x.tenant?.name || x.tenant_id || '—'} • {x.count}</li>))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{confirmPayload?.title || 'Confirmar'}</DialogTitle>
          <DialogDescription>
            {confirmPayload?.type === 'bulk' ? `Você está prestes a remover ${confirmPayload?.usageIds.length || 0} uso(s) deste cupom.` : 'Você está prestes a remover este uso do cupom.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={()=> setConfirmOpen(false)} disabled={confirmLoading}>Cancelar</Button>
          <Button onClick={confirmExecute} disabled={confirmLoading}>{confirmLoading ? 'Removendo...' : 'Confirmar'}</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
